import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import HomePage from "@/pages/HomePage";
import BrowseWorkersPage from "@/pages/BrowseWorkersPage";
import RequestServicePage from "@/pages/RequestServicePage";
import JoinAsProPage from "@/pages/JoinAsProPage";
import LeadsPage from "@/pages/LeadsPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import ProfilePage from "@/pages/ProfilePage";
import ContractorDashboard from "@/pages/ContractorDashboard";
import PublicJobBoard from "@/pages/PublicJobBoard";
import ServiceLocationPage from "@/pages/ServiceLocationPage";
import CityServicePage from "@/pages/CityServicePage";
import FAQPage from "@/pages/FAQPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import NotificationsPage from "@/pages/NotificationsPage";
import LiveChatWidget from "@/components/LiveChatWidget";
import usePageTracking from "@/hooks/usePageTracking";

// Live Chat wrapper that uses auth context
function LiveChatWrapper() {
  const { user, isAuthenticated } = useAuth();
  
  if (!isAuthenticated || !user) return null;
  
  const userType = user.role === 'worker' ? 'worker' : 'customer';
  return <LiveChatWidget user={user} userType={userType} />;
}

// Router component that handles OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Track page views
  usePageTracking();
  
  // CRITICAL: Check URL fragment for session_id BEFORE checking existing session
  // This prevents race conditions with AuthProvider's checkAuth
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/browse" element={<BrowseWorkersPage />} />
      <Route path="/request" element={<RequestServicePage />} />
      <Route path="/join" element={<JoinAsProPage />} />
      <Route path="/leads" element={<LeadsPage />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
      <Route path="/profile/:workerId" element={<ProfilePage />} />
      <Route path="/dashboard" element={<ContractorDashboard />} />
      <Route path="/jobs" element={<PublicJobBoard />} />
      <Route path="/services/:location/:service" element={<ServiceLocationPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      {/* New SEO-optimized city/service pages - MUST be last due to catch-all pattern */}
      <Route path="/:citySlug/:service" element={<CityServicePage />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-white">
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <LiveChatWrapper />
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </div>
    </HelmetProvider>
  );
}

export default App;
